import {
  Money04Icon,
  ArrowLeftRightIcon,
  Edit02Icon,
  ArrowDataTransferHorizontalIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonVariant, ButtonSize } from "shared/components";
import { MenuRow } from "views/applications-id/components/LayoutHeader/components/MenuRow";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import {
  BankStatements,
  EditIncomeModalData,
  EditTransactionsModalData
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/types";
import { CombinedIncome } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import {
  getIncomeActualNetAndGrossDollars,
  isValidBankStatement,
  resolveMergedPlaidBankSource
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/utils";
import { useMemo } from "react";
import { IncomeSource, IncomeStatus } from "shared/types";
import { ReverifyAttempt } from "../../../ReverifySlice/types";
import {
  isReverifyTabABankStatement,
  isReverifyTabArchived
} from "../../../ReverifySlice/utils";
import { SelectBankStreamModalData } from "views/applications-id-report/slices/CombinedIncomeReportSlice/components/SelectBankStreamModal";

const EditButton = ({
  isDisabled,
  onClick,
  disabledReason
}: {
  isDisabled: boolean;
  disabledReason?: string;
  onClick?: () => void;
}) => {
  return (
    <Button
      variant={ButtonVariant.BASE}
      size={ButtonSize.XS}
      HugeIcon={Edit02Icon}
      className="shadow-xs w-[80px] !gap-2"
      disabled={isDisabled}
      tooltip={isDisabled && disabledReason ? disabledReason : undefined}
      onClick={onClick}
    >
      Edit
    </Button>
  );
};

export const EditIncomeDropdownMenu = ({
  setShowEditIncomeAmountModal,
  setShowEditIncomeTransactionsDrawer,
  setShowSelectBankStreamModal,
  selectedReverifyAttempt,
  income,
  allBankStatements,
  plaidBankIncomeSources,
  disabled,
  disabledReason
}: {
  setShowEditIncomeAmountModal?: (data: false | EditIncomeModalData) => void;
  setShowEditIncomeTransactionsDrawer?: (
    data: false | EditTransactionsModalData
  ) => void;
  setShowSelectBankStreamModal?: (
    data: false | SelectBankStreamModalData
  ) => void;
  selectedReverifyAttempt?: ReverifyAttempt;
  income: CombinedIncome;
  allBankStatements?: BankStatements;
  plaidBankIncomeSources?: IncomeSource[];
  disabled: boolean;
  disabledReason?: string;
}) => {
  const { actualVerifiedNetMonthlyDollars, actualVerifiedGrossMonthlyDollars } =
    getIncomeActualNetAndGrossDollars(income, selectedReverifyAttempt);

  const isArchived = isReverifyTabArchived(selectedReverifyAttempt);
  const isBankStatement = isReverifyTabABankStatement(selectedReverifyAttempt);

  const filteredBankStatements = useMemo(
    () =>
      allBankStatements
        ? Object.values(allBankStatements).filter((bankStatement) =>
            isValidBankStatement(bankStatement)
          )
        : [],
    [allBankStatements]
  );

  const isTransactionsEditAvailable = Boolean(
    !isArchived && isBankStatement && filteredBankStatements?.length
  );

  const plaidBankSource = useMemo(
    () => resolveMergedPlaidBankSource(income, plaidBankIncomeSources ?? []),
    [plaidBankIncomeSources, income]
  );

  const isIncomeInProgress =
    income.status === IncomeStatus.Pending ||
    income.status === IncomeStatus.Processing;
  const isIncomeDeleted = income.status === IncomeStatus.Deleted;

  const isChangeStreamAvailable = Boolean(
    !isArchived &&
    !isIncomeInProgress &&
    !isIncomeDeleted &&
    plaidBankSource &&
    plaidBankSource.items.length > 1
  );

  if (!isTransactionsEditAvailable && !isChangeStreamAvailable)
    return (
      <EditButton
        isDisabled={disabled}
        disabledReason={disabledReason}
        onClick={() =>
          setShowEditIncomeAmountModal?.({
            grossMonthlyIncomeDollars: actualVerifiedGrossMonthlyDollars,
            netMonthlyIncomeDollars: actualVerifiedNetMonthlyDollars,
            hasNetMonthlyIncome: income.type !== "offer_letter",
            isTotal: false,
            id: income.id,
            additional_data: income.additional_data,
            type: income.type
          })
        }
      />
    );

  const assignedItemId =
    plaidBankSource?.items.find((item) => item.income_id === income.id)?.id ??
    null;

  const dropdownActions = [
    {
      title: <MenuRow text="Edit income amount" HugeIcon={Money04Icon} />,
      className: "!py-2.5",
      disabled,
      tooltip: disabledReason || undefined,
      onClick: () =>
        setShowEditIncomeAmountModal?.({
          grossMonthlyIncomeDollars: actualVerifiedGrossMonthlyDollars,
          netMonthlyIncomeDollars: actualVerifiedNetMonthlyDollars,
          hasNetMonthlyIncome: income.type !== "offer_letter",
          isTotal: false,
          id: income.id,
          additional_data: income.additional_data,
          type: income.type
        })
    },
    ...(isTransactionsEditAvailable
      ? [
          {
            title: (
              <MenuRow
                text="Edit income transactions"
                HugeIcon={ArrowLeftRightIcon}
              />
            ),
            className: "!py-2.5",
            disabled,
            tooltip: disabledReason || undefined,
            onClick: () =>
              setShowEditIncomeTransactionsDrawer?.({
                id: income.id,
                type: income.type,
                additional_data: income.additional_data
              })
          }
        ]
      : []),
    ...(isChangeStreamAvailable && plaidBankSource
      ? [
          {
            title: (
              <MenuRow
                text="Change income source"
                HugeIcon={ArrowDataTransferHorizontalIcon}
              />
            ),
            className: "!py-2.5",
            disabled,
            tooltip: disabledReason || undefined,
            onClick: () =>
              setShowSelectBankStreamModal?.({
                incomeId: income.id,
                income,
                availableSource: plaidBankSource,
                assignedItemId
              })
          }
        ]
      : [])
  ];

  return (
    <ContextMenuPopover
      borderLess={false}
      trigger={
        <EditButton isDisabled={disabled} disabledReason={disabledReason} />
      }
      actions={dropdownActions}
      placement="bottom-end"
      contentClassName="w-[275px]"
    />
  );
};
